"use client";

import { useState, useMemo, useEffect, useRef } from "react";
import moment from "moment";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Box,
  Chip,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  LinearProgress,
  useTheme,
  alpha,
  Paper,
  ClickAwayListener,
  Popper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControlLabel,
  Checkbox,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  Group as Users,
  AccessTime as Clock,
  CheckCircle as CheckCircleIcon,
  Cancel as XCircleIcon,
  FilterAlt as Filter,
} from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/Clear";
import { ErrorToaster } from "../../Components/Toaster/Toaster";
import AccountSettingServices from "../../Api/AccountSettingServices/AccountSettingServices.index";
import { useLocation } from "react-router-dom";
import { Colors } from "../../Assets/Styles/Colors";
import { useAuth } from "../../Context/UseContext";
import { CSVLink } from "react-csv";
import { Controller, useForm } from "react-hook-form";

const getDaysInMonth = (selectedDate) => {
  const days = [];
  const year = selectedDate.year();
  const month = selectedDate.month();
  const daysInMonth = selectedDate.daysInMonth();

  for (let i = 1; i <= daysInMonth; i++) {
    days.push(moment([year, month, i]));
  }

  return days;
};

export default function AttendanceTable() {
  const theme = useTheme();
  const [selectedDate, setSelectedDate] = useState(moment());
  const [userAttendanceData, setUserAttendanceData] = useState([]);
  const { state } = useLocation();
  const { user } = useAuth();

  const [anchorEl, setAnchorEl] = useState(null);
  const [popperData, setPopperData] = useState(null);

  const [csvLoading, setCsvLoading] = useState(false);
  const [disabledFields, setDisabledFields] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const csvLink = useRef();
  const [open, setOpen] = useState(false);

  const { control, handleSubmit, reset, register, watch, setValue } = useForm();

  // const [selectedDate, setSelectedDate] = useState(moment());

  // onChange handler for month picker
  const handleMonthChange = (date) => {
    setSelectedDate(moment(date));
  };

  const [loading, setLoading] = useState(false);

  const getTimeReport = async (month, year) => {
    try {
      const response = await AccountSettingServices.checkLog(
        month,
        year,
        state ? state?._id : user?.id
      );
      setUserAttendanceData(response?.data);

      console.log(response);
    } catch (error) {
      console.log(error);
    }
  };
  const transformedData =
    userAttendanceData?.attendanceLog?.map((day) => ({
      date: moment(day.date).format("DD-MM-YYYY"),
      status:
        day.status === "present"
          ? "Present"
          : day.status === "absent"
          ? "Absent"
          : "Leave",
      duration: day.shifts,
    })) || [];

  console.log(transformedData);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const month = selectedDate.format("MM");
      const year = selectedDate.format("YYYY");
      await getTimeReport(month, year);
      setLoading(false);
    };
    fetchData();
  }, [selectedDate]);

  // Calculate summary statistics
  const presentCount =
    transformedData?.filter((day) => day.status === "Present")?.length ?? 0;
  const absentCount =
    transformedData?.filter((day) => day.status === "Absent")?.length ?? 0;
  const totalDays = transformedData?.length ?? 0;

  const attendanceRate = totalDays > 0 ? (presentCount / totalDays) * 100 : 0;

  // Format date
  const formatDate = () => {
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date().toLocaleDateString("en-US", options);
  };

  const handleChipClick = (event, data) => {
    setAnchorEl(event.currentTarget);
    setPopperData(data);
  };

  const handlePopperClose = () => {
    setAnchorEl(null);
    // setPopperData(null);
  };

  const getAdjustedAbsentData = (selectedDate, absentCount, totalDays) => {
    const year = selectedDate.year();
    const month = selectedDate.month();
    const daysInMonth = moment([year, month]).daysInMonth();

    const today = moment();
    const isCurrentMonth = today.year() === year && today.month() === month;

    // Use today's date if selectedDate doesn't represent current day
    const referenceDate = isCurrentMonth
      ? moment.max(selectedDate, today) // ensure date is not before today
      : selectedDate;

    let sundaysCount = 0;
    let futureDaysCount = 0;

    for (let i = 1; i <= daysInMonth; i++) {
      const date = moment([year, month, i]);

      // Count Sundays before or on selected/current date
      if (
        date.day() === 0 &&
        (!isCurrentMonth || date.isSameOrBefore(referenceDate, "day"))
      ) {
        sundaysCount++;
      }

      // Count future days (after selected/current date)
      if (isCurrentMonth && date.isAfter(referenceDate, "day")) {
        futureDaysCount++;
      }
    }

    const totalToSubtract = sundaysCount + futureDaysCount;
    const adjustedAbsentCount = Math.max(absentCount - totalToSubtract, 0);
    const adjustedAbsentPercentage =
      totalDays > 0 ? Math.round((adjustedAbsentCount / totalDays) * 100) : 0;

    return { adjustedAbsentCount, adjustedAbsentPercentage };
  };

  const { adjustedAbsentCount, adjustedAbsentPercentage } = useMemo(
    () => getAdjustedAbsentData(selectedDate, absentCount, totalDays),
    [selectedDate, absentCount, totalDays]
  );

  useEffect(() => {
    if (csvData.length > 0) {
      csvLink?.current.link.click();
    }
  }, [csvData]);
  console.log(transformedData, "transformedData");

  const downloadCsv = () => {
    setCsvLoading(true);
    try {
      const csvHeaders = [
        // { label: "Name", key: "name" },
        { label: "Date", key: "date" },
        { label: "Status", key: "status" },
        { label: "Shift Duration", key: "duration" },
        { label: "Total Hours Worked", key: "total" },
      ];

      const csvRows = transformedData.map((log) => ({
        date: log.date,
        status: log.status,
        duration:
          log.duration?.length > 0
            ? moment
                .utc()
                .startOf("day")
                .add(log.duration[0]?.check_in, "minutes")
                .format("hh:mm A") +
              "-" +
              moment
                .utc()
                .startOf("day")
                .add(log.duration[0]?.check_out, "minutes")
                .format("hh:mm A")
            : "0",
        total: log.duration?.length > 0 ? log.duration[0]?.hours_worked : "0",
      }));

      setCsvData([
        csvHeaders.map((header) => header.label),
        ...csvRows.map((row) => csvHeaders.map((header) => row[header.key])),
      ]);
    } catch (error) {
      console.error("Error generating CSV:", error);
    } finally {
      setCsvLoading(false);
    }
  };
  const handleRightClick = (e, data) => {
    console.log(data, "data");
    e.preventDefault();
    setOpen(true);
    setValue(
      "check_in",
      data?.shifts[0]?.check_in
        ? moment()
            .startOf("day")
            .add(data.shifts[0].check_in, "minutes")
            .format("HH:mm")
        : ""
    );
    setValue(
      "check_out",
      data?.shifts[0]?.check_out
        ? moment()
            .startOf("day")
            .add(data.shifts[0].check_out, "minutes")
            .format("HH:mm")
        : ""
    );
    setValue("is_wfh", data?.is_wfh || false);
    setValue("wfh_reason", data?.wfh_reason || "");
    setValue("wfh_approval", data?.wfh_approval || "");
  };

  const onSubmit = async (data) => {
    console.log(data);

    const isWfh = data.is_wfh;

    const checkInMinutes = isWfh
      ? null
      : data.check_in
      ? moment.duration(data.check_in).asMinutes()
      : null;

    const checkOutMinutes = isWfh
      ? null
      : data.check_out
      ? moment.duration(data.check_out).asMinutes()
      : null;

    if (!popperData?.date) {
      ErrorToaster(
        "Please select a date from the calendar to update attendance."
      );
      return;
    }
    const formattedData = {
      ...data,
      check_in: checkInMinutes,
      check_out: checkOutMinutes,
      user_id: state ? state?._id : user?.id,
      date: moment(popperData?.date).format("YYYY-MM-DD"),
    };

    try {
      const result = await AccountSettingServices.updateAttendance(
        formattedData
      );
      console.log("Submitted Attendance:", result);
      const month = selectedDate.format("MM");
      const year = selectedDate.format("YYYY");
      await getTimeReport(month, year);
    } catch (error) {
      ErrorToaster(error);
      console.log(error);
    } finally {
      setOpen(false);
    }
  };

  const markAsWfh = watch("is_wfh");

  useEffect(() => {
    if (markAsWfh == true) {
      setDisabledFields(true);
    } else {
      setDisabledFields(false);
    }
  }, [markAsWfh]);

    function timeToMinutes(timeStr) {
      const hours = parseInt(timeStr.match(/(\d+)\s*hour/)?.[1] || 0);
      const minutes = parseInt(timeStr.match(/(\d+)\s*minute/)?.[1] || 0);
      return hours * 60 + minutes;
    }
   
  const totalMinutes = userAttendanceData?.attendanceLog?.reduce((sum, log) => {
    const shift = log.shifts[0];
    if (shift?.check_in != null && shift?.check_out != null) {
      return sum + timeToMinutes(shift.hours_worked);
    }
    return sum;
  }, 0);

  return (
    <>
      <Box
        sx={{
          margin: "0 auto",
          p: { xs: 2, md: 4 },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                color: Colors.primary,
                mb: 0.5,
              }}
            >
              {state?.name} Attendance Dashboard
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <CalendarIcon fontSize="small" />
              {formatDate()}
            </Typography>
          </Box>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                height: "100%",
                border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: theme.shadows[4],
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    Present Days
                  </Typography>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: theme.palette.success.main,
                    }}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </Avatar>
                </Box>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{ fontWeight: 700, mb: 1, fontSize: "30px" }}
                >
                  {presentCount}
                </Typography>
                <Typography
                  variant="body2"
                  color="success.main"
                  sx={{ fontWeight: 500 }}
                >
                  {Math.round((presentCount / totalDays) * 100)}% of total days
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                height: "100%",
                border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
                boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",

                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: theme.shadows[4],
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    Absent Days
                  </Typography>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                      color: theme.palette.error.main,
                    }}
                  >
                    <XCircleIcon fontSize="small" />
                  </Avatar>
                </Box>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{ fontWeight: 700, mb: 1, fontSize: "30px" }}
                >
                  {adjustedAbsentCount}
                </Typography>
                <Typography
                  variant="body2"
                  color="error.main"
                  sx={{ fontWeight: 500 }}
                >
                  {adjustedAbsentPercentage}% of total days
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                height: "100%",
                border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
                boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",

                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: theme.shadows[4],
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Work Hours
                  </Typography>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      color: theme.palette.warning.main,
                    }}
                  >
                    <Clock fontSize="small" />
                  </Avatar>
                </Box>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{ fontWeight: 700, mb: 1, fontSize: "30px" }}
                >
                  {Math.floor(totalMinutes / 60)} hours {totalMinutes % 60}{" "}
                  minutes
                </Typography>
                <Typography
                  variant="body2"
                  color="warning.main"
                  sx={{ fontWeight: 500 }}
                >
                  For {selectedDate.format("MMMM YYYY")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        {user?.role == "company" && (
          <>
            <Card
              elevation={0}
              sx={{
                mb: 4,
                borderRadius: 2,
                boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Filter fontSize="small" color={theme.palette.primary.main} />
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{ ml: 1, fontWeight: 600 }}
                  >
                    Filter Attendance Records
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Select Month"
                      type="month"
                      variant="outlined"
                      size="small"
                      value={selectedDate.format("YYYY-MM")}
                      onChange={(e) => {
                        setSelectedDate(moment(e.target.value, "YYYY-MM"));
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon
                              fontSize="small"
                              color={theme.palette.text.secondary}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <CSVLink
              ref={csvLink}
              data={csvData}
              filename={`${
                state?.name + moment().format("DD-MMM-YYYY_HHmmss")
              }.csv`}
              target="_blank"
            ></CSVLink>
            <Box
              sx={{
                display: "flex",
                justifyContent: " end",
                width: "100%",
                mb: 2,
              }}
            >
              <Button
                sx={{
                  backgroundColor: Colors.primary,
                  color: Colors.white,
                  ":hover": {
                    backgroundColor: Colors.primary,
                    color: Colors.white,
                  },
                }}
                onClick={() => downloadCsv()}
                loading={csvLoading}
              >
                Download Excel
              </Button>
            </Box>
          </>
        )}

        {/* Attendance Progress */}
        {/* <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle2">Overall Attendance Rate</Typography>
          <Typography variant="subtitle2" fontWeight={600}>
            {Math.round(attendanceRate)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={attendanceRate}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: alpha(Colors.primary, 0.1),
            "& .MuiLinearProgress-bar": {
              borderRadius: 4,
              background: `linear-gradient(90deg, ${Colors.primary}, ${Colors.primary})`,
            },
          }}
        />
      </Box> */}

        {/* Table */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
          {loading && <LinearProgress />}
          <TableContainer sx={{ maxHeight: 500, overflow: "auto" }}>
            <Table stickyHeader aria-label="attendance table">
              <TableHead>
                <TableRow>
                  {getDaysInMonth(selectedDate).map((day) => {
                    const isSunday = day.day() === 0;
                    return (
                      <TableCell
                        key={day.format("D")}
                        align="center"
                        sx={{
                          fontWeight: 600,
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          color: Colors.primary,
                          minWidth: 100,
                          padding: "8px 4px",
                          border: "none !important",
                          fontSize: "0.75rem",
                          lineHeight: 2,
                          whiteSpace: "nowrap",
                          zIndex: 0,
                        }}
                      >
                        {day.format("D")} <br />
                        {day.format("ddd")}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>

              <TableBody>
                <TableRow
                  hover
                  sx={{
                    transition: "background-color 0.2s",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.02),
                    },
                  }}
                >
                  {getDaysInMonth(selectedDate).map((day) => {
                    const dateStr = day.format("YYYY-MM-DD");
                    const isSunday = day.day() === 0;

                    const attendanceForDay =
                      userAttendanceData.attendanceLog?.find(
                        (att) =>
                          moment(att.date).format("YYYY-MM-DD") === dateStr
                      );

                    const status = isSunday
                      ? "Holiday"
                      : attendanceForDay?.is_wfh
                      ? "WFH"
                      : attendanceForDay?.status === "present"
                      ? attendanceForDay?.shifts[0]?.check_out
                        ? attendanceForDay?.shifts[0]?.hours_worked
                        : "Present"
                      : "Absent";

                    return (
                      <TableCell key={dateStr} align="center" sx={{ py: 1 }}>
                        <Chip
                          label={status}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            borderRadius: 1,
                            width: 100,
                            py: 4,
                            fontSize: "10px",
                            backgroundColor:
                              status === "Holiday"
                                ? "#f0f0f0"
                                : status === "WFH"
                                ? "#fee87f"
                                : status === "Present"
                                ? "#daffd3"
                                : status === "Absent"
                                ? "#fee6e4"
                                : "#e6f2ff",
                            border: `1px solid ${
                              status === "Holiday"
                                ? "grey"
                                : status === "WFH"
                                ? "yellow"
                                : status === "Present"
                                ? "green"
                                : status === "Absent"
                                ? "red"
                                : "#e6f2ff"
                            }`,
                          }}
                          onClick={(e) =>
                            attendanceForDay
                              ? handleChipClick(e, attendanceForDay)
                              : null
                          }
                          onContextMenu={(e) => {
                            if (user?.role == "company") {
                              handleRightClick(e, attendanceForDay);
                              setPopperData(attendanceForDay);
                            }
                          }}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="top"
        disablePortal
      >
        <ClickAwayListener onClickAway={handlePopperClose}>
          <Paper
            sx={{
              p: 2,
              border: "1px solid #ddd",
              boxShadow: 3,
              minWidth: 200,
              backgroundColor: "#fff",
              // zIndex: 1300,
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Date:{" "}
              {popperData?.date &&
                moment(popperData.date).format("DD MMM YYYY")}
            </Typography>
            {popperData?.is_wfh ? (
              <>
                <Typography variant="body2">
                  WFH Approval: {popperData?.wfh_approval || ""}
                </Typography>
                <Typography variant="body2">
                  WFH Reason: {popperData?.wfh_reason || ""}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2">
                  Check-in:{" "}
                  {popperData?.shifts[0]?.check_in != null
                    ? moment
                        .utc()
                        .startOf("day")
                        .add(popperData?.shifts[0]?.check_in, "minutes")
                        .format("hh:mm A")
                    : "N/A"}
                </Typography>
                <Typography variant="body2">
                  Check-out:{" "}
                  {popperData?.shifts[0]?.check_out != null
                    ? moment
                        .utc()
                        .startOf("day")
                        .add(popperData?.shifts[0]?.check_out, "minutes")
                        .format("hh:mm A")
                    : "N/A"}
                </Typography>
              </>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
      <Dialog
        maxWidth="md"
        open={open}
        onClose={() => {
          setOpen(false);
          // reset();
          setDisabledFields(false);
          reset((formValues) => ({
            ...formValues,
            is_wfh: false,
            wfh_reason: "",
            wfh_approval: "",
          }));
        }}
        sx={{
          "& .MuiDialog-paper": {
            width: "80%",
            height: "auto",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>
          Update Attendance for {moment(popperData?.date).format("DD-MM-YYYY")}
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: "15px !important",
          }}
        >
          {!disabledFields && (
            <>
              <Controller
                name="check_in"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Check In"
                    type="time"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputProps={{
                      endAdornment: field.value ? (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => field.onChange("")}
                            edge="end"
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                )}
              />

              <Controller
                name="check_out"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Check Out"
                    type="time"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputProps={{
                      endAdornment: field.value ? (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => field.onChange("")}
                            edge="end"
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                )}
              />
            </>
          )}
          <Controller
            name="is_wfh"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label="Mark as Work From Home"
              />
            )}
          />

          {disabledFields && (
            <>
              <Controller
                name="wfh_reason"
                control={control}
                rules={{ required: "WFH Reason is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="WFH Reason"
                    type="text"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="wfh_approval"
                control={control}
                rules={{ required: "WFH Approval is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="WFH Approval"
                    type="text"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            sx={{
              backgroundColor: Colors.primary,
              color: Colors.white,
              ":hover": {
                backgroundColor: Colors.primary,
                color: Colors.white,
              },
            }}
            onClick={() => {
              setOpen(false);
              setDisabledFields(false);
              // reset();
              reset((formValues) => ({
                ...formValues,
                is_wfh: false,
                wfh_reason: "",
                wfh_approval: "",
              }));
            }}
          >
            Cancel
          </Button>
          <Button
            sx={{
              backgroundColor: Colors.primary,
              color: Colors.white,
              ":hover": {
                backgroundColor: Colors.primary,
                color: Colors.white,
              },
            }}
            variant="contained"
            onClick={handleSubmit(onSubmit)}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
